import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FieldWrapper, Button } from '../../fields/css';
import OfficePhoneLinePopup from './OfficePhoneLinePopup';
import OfficePhoneLinesTable from './OfficePhoneLinesTable';

const OfficePhoneLine = ({ 
  fieldName,
  fields, 
  error,
  officePhoneLinesData = [],
  onOfficePhoneLinesChange,
  setIsFormTouched,
  setShowDownloadQuoteFooter
}) => {
  const { label } = fields;
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [phoneLines, setPhoneLines] = useState(officePhoneLinesData);
  const [editingLine, setEditingLine] = useState(null); // Track which line is being edited

  // Sync with parent state. Bail out when the incoming data matches the current
  // state so we don't trigger an endless render loop when the parent passes a
  // fresh array reference (e.g. a new `[]` default) on every render.
  useEffect(() => {
    const next = officePhoneLinesData || [];
    setPhoneLines(prev =>
      JSON.stringify(prev) === JSON.stringify(next) ? prev : next
    );
  }, [officePhoneLinesData]);

  const handleButtonClick = () => {
    setEditingLine(null); // Reset editing state when opening for new line
    setIsModalOpened(true);
  };

  const handleModalClose = () => {
    setIsModalOpened(false);
    setEditingLine(null); // Reset editing state when closing
    setIsFormTouched(true);
    setShowDownloadQuoteFooter(false);
  };

  const handleAddLine = (lineData) => {
    if (editingLine !== null) {
      // Update existing line
      const updatedLines = phoneLines.map((line, index) => 
        (line.id === editingLine.id || index === editingLine.index) 
          ? { ...lineData, id: editingLine.id || line.id || Date.now().toString() }
          : line
      );
      setPhoneLines(updatedLines);
      
      // Notify parent component
      if (onOfficePhoneLinesChange) {
        onOfficePhoneLinesChange(updatedLines);
      }
    } else {
      // Add new line
      const newLineData = {
        ...lineData,
        id: Date.now().toString()
      };
      
      const updatedLines = [...phoneLines, newLineData];
      setPhoneLines(updatedLines);
      
      // Notify parent component
      if (onOfficePhoneLinesChange) {
        onOfficePhoneLinesChange(updatedLines);
      }
    }
    
    handleModalClose();
  };

  const handleEditLine = (lineData) => {
    const lineIndex = phoneLines.findIndex((line, index) => 
      (lineData.id && line.id === lineData.id) || 
      (!lineData.id && index === phoneLines.indexOf(lineData))
    );
    
    setEditingLine({
      id: lineData.id,
      index: lineIndex >= 0 ? lineIndex : phoneLines.indexOf(lineData)
    });
    setIsModalOpened(true);
  };

  const handleDeleteLine = (lineId) => {
    const updatedLines = phoneLines.filter((line, index) => {
      // If line has an id, compare by id; otherwise compare by index
      if (line.id) {
        return line.id !== lineId;
      }
      return index.toString() !== lineId.toString();
    });
    setPhoneLines(updatedLines);
    
    // Notify parent component
    if (onOfficePhoneLinesChange) {
      onOfficePhoneLinesChange(updatedLines);
    }
    setIsFormTouched(true);
    setShowDownloadQuoteFooter(false);
  };

  return (
    <FieldWrapper>
      <Button
        type="button"
        name={fieldName}
        onClick={handleButtonClick}
        className="frg-button color-green"
      >
        <span>{label}</span>
      </Button>
      
      <OfficePhoneLinePopup
        isOpen={isModalOpened}
        onClose={handleModalClose}
        onAddLine={handleAddLine}
        editingLine={editingLine !== null ? phoneLines.find((line, index) => 
          (editingLine.id && line.id === editingLine.id) || 
          (!editingLine.id && index === editingLine.index)
        ) : null}
      />
      
      <OfficePhoneLinesTable
        officePhoneLinesData={phoneLines}
        onEditLine={handleEditLine}
        onDeleteLine={handleDeleteLine}
      />
      
      {error && <div style={{ color: 'red', marginTop: '5px' }}>{error}</div>}
    </FieldWrapper>
  );
};

OfficePhoneLine.propTypes = {
  fieldName: PropTypes.string.isRequired,
  fields: PropTypes.shape({
    label: PropTypes.string.isRequired
  }).isRequired,
  error: PropTypes.string,
  officePhoneLinesData: PropTypes.array,
  onOfficePhoneLinesChange: PropTypes.func,
  setIsFormTouched: PropTypes.func,
  setShowDownloadQuoteFooter: PropTypes.func
};

export default OfficePhoneLine;
